/**
 * Liore shoppable article: every product mentioned in the text gets its
 * add-to-cart card under the paragraph that first mentions it.
 *
 * The merchant marks a mention by linking the words to the product in the
 * article editor. `liore-article-content.liquid` pre-renders one hidden card
 * per linked product inside a <template>, so price, currency and stock come
 * from the server for the visitor's market and nothing is fetched on tap.
 * Without JavaScript the links stay plain links to the product page.
 *
 * Like `liore-social-rail.js`, this module imports nothing from `@theme/*`
 * so its pure helpers can be unit tested under `node --test`.
 */

const BLOCK_TAGS = new Set(['P', 'LI', 'BLOCKQUOTE', 'H2', 'H3', 'H4', 'H5', 'H6', 'FIGURE']);

/**
 * The product handle a link points at, or '' when it is not one of our
 * product pages. Accepts the shapes the editor and merchants produce:
 * `/products/x`, a locale prefix, a collection-scoped path, and absolute
 * URLs on this shop — with or without query and fragment.
 *
 * A full link counts when it points at any address the shop answers on
 * (liore.ee, the myshopify address, or the page's own host): Shopify opens
 * draft previews on the myshopify address while editors paste liore.ee links.
 *
 * @param {string | null | undefined} href
 * @param {string} origin - The page's origin.
 * @param {string[]} [hosts] - Every host the shop answers on.
 * @returns {string}
 */
export function productHandleFromHref(href, origin, hosts = []) {
  const target = linkTarget(href, origin, hosts);
  return target?.kind === 'product' ? target.handle : '';
}

/**
 * What a link in the article points at: one of our products, collections or
 * pages, or null for anything else (other sites, the blog, list pages).
 * Same address rules as productHandleFromHref.
 *
 * @param {string | null | undefined} href
 * @param {string} origin - The page's origin.
 * @param {string[]} [hosts] - Every host the shop answers on.
 * @returns {{ kind: 'product' | 'collection' | 'page', handle: string } | null}
 */
export function linkTarget(href, origin, hosts = []) {
  const raw = String(href ?? '').trim();
  if (!raw || raw.startsWith('#')) return null;

  let url;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  const bare = (host) => String(host ?? '').trim().toLowerCase().replace(/^www\./, '');
  const shopHosts = new Set([new URL(origin).host, ...hosts].map(bare).filter(Boolean));
  if (!shopHosts.has(bare(url.host))) return null;

  // A product wins over the collection it is browsed through.
  const match =
    /\/products\/([^/]+)\/?$/.exec(url.pathname) ??
    /^(?:\/[a-z]{2}(?:-[a-z]{2,4})?)?\/(collections|pages)\/([^/]+)\/?$/i.exec(url.pathname);
  if (!match) return null;
  const kind = match.length === 2 ? 'product' : match[1].toLowerCase() === 'collections' ? 'collection' : 'page';
  try {
    return { kind, handle: decodeURIComponent(match[match.length - 1]).toLowerCase() };
  } catch {
    return null;
  }
}

/**
 * The outermost list (UL/OL) in the article body that holds an element, or
 * null. Collection and page cards for links in a list go after the list, in
 * one row, so the list stays readable.
 *
 * @param {{ tagName: string, parentElement: any }} el
 * @param {any} root - The article body; never walk past it.
 */
export function listAncestor(el, root) {
  let found = null;
  for (let node = el.parentElement; node && node !== root; node = node.parentElement) {
    if (node.tagName === 'UL' || node.tagName === 'OL') found = node;
  }
  return found;
}

/**
 * Which links get a card: the first link to each product, collection or
 * page, in reading order. Something named again later is not shown twice.
 *
 * @param {string[]} handles - One key per link (e.g. 'product:x'); '' for links without a card.
 * @returns {number[]} Indexes of the links that get a card.
 */
export function firstMentions(handles) {
  const seen = new Set();
  const picked = [];
  handles.forEach((handle, i) => {
    if (!handle || seen.has(handle)) return;
    seen.add(handle);
    picked.push(i);
  });
  return picked;
}

/**
 * The block-level element a card should follow: the paragraph, list item or
 * quote holding the mention. A card inside a <p> would be invalid HTML and
 * would break the line the reader is on.
 *
 * @param {{ tagName: string, parentElement: any }} el
 * @param {any} root - The article body; never walk past it.
 */
export function blockAncestor(el, root) {
  let node = el;
  while (node.parentElement && node.parentElement !== root) {
    if (BLOCK_TAGS.has(node.tagName) && node !== el) return node;
    node = node.parentElement;
  }
  return BLOCK_TAGS.has(node.tagName) || node === el ? node : el;
}

/**
 * Add one unit of a variant and tell the theme, the same way the social rail
 * does. The theme's listeners read `event.promise` and `event.action` (see
 * cart-icon.js and cart-drawer.js); a bare CustomEvent is ignored by both.
 *
 * `source` goes on the cart line as a hidden `_source` property, so orders
 * show the product was added from an article.
 *
 * @param {{ id: number, source?: string, fetchImpl?: typeof fetch, routes?: Record<string, string>, dispatch?: (e: Event) => void }} options
 */
export async function addToCart({ id, source, fetchImpl = fetch, routes = {}, dispatch } = {}) {
  if (!Number(id)) throw new Error('missing variant id');

  const response = await fetchImpl(routes.cart_add_url || '/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      items: [source ? { id: Number(id), quantity: 1, properties: { _source: source } } : { id: Number(id), quantity: 1 }],
    }),
  });
  if (!response.ok) throw new Error(String(response.status));

  const cartUrl = routes.cart_url || '/cart';
  const event =
    typeof CustomEvent === 'function'
      ? new CustomEvent('shopify:cart:lines-update', { bubbles: true })
      : { type: 'shopify:cart:lines-update' };
  event.action = 'add';
  event.context = 'product';
  event.promise = fetchImpl(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
    .then((r) => r.json())
    .then((cart) => ({
      cart: { ...cart, totalQuantity: cart.item_count },
      // `source` lets product-form.js skip a redundant #refreshCart().
      detail: { itemCount: cart.item_count, source: 'liore-article' },
    }));
  (dispatch ?? ((e) => document.dispatchEvent(e)))(event);
}

function themeRoutes() {
  // Bare `Theme`: scripts.liquid declares it with `const`, so it is never a
  // property of window.
  // eslint-disable-next-line no-undef
  return (typeof Theme !== 'undefined' && Theme?.routes) || {};
}

if (typeof customElements !== 'undefined' && !customElements.get('liore-article-mentions')) {
  class LioreArticleMentions extends HTMLElement {
    connectedCallback() {
      const body = this.querySelector('[data-article-body]');
      if (!body) return;

      const templates = new Map();
      for (const kind of ['product', 'collection', 'page']) {
        for (const t of this.querySelectorAll(`template[data-article-${kind}]`)) {
          templates.set(`${kind}:${t.getAttribute(`data-article-${kind}`)}`, t);
        }
      }
      if (templates.size === 0) return;

      const hosts = (this.dataset.shopHosts || '').split(',');
      const links = [...body.querySelectorAll('a[href]')];
      const keys = links.map((link) => {
        const target = linkTarget(link.getAttribute('href'), location.origin, hosts);
        const key = target ? `${target.kind}:${target.handle}` : '';
        return templates.has(key) ? key : '';
      });

      const rails = new Map();
      for (const i of firstMentions(keys)) {
        const link = links[i];
        const card = templates.get(keys[i]).content.firstElementChild?.cloneNode(true);
        if (!card) continue;
        card.setAttribute('data-article-card', '');
        link.classList.add('liore-mention');

        // Collections and pages linked from a list share one row after it.
        const list = keys[i].startsWith('product:') ? null : listAncestor(link, body);
        if (list) {
          let rail = rails.get(list);
          if (!rail) {
            rail = document.createElement('div');
            rail.className = 'liore-link-rail';
            rail.setAttribute('data-article-card', '');
            list.after(rail);
            rails.set(list, rail);
          }
          rail.append(card);
          continue;
        }

        // Several cards for one paragraph stack in reading order.
        let after = blockAncestor(link, body);
        while (after.nextElementSibling?.hasAttribute?.('data-article-card')) after = after.nextElementSibling;
        after.after(card);
      }

      this.addEventListener('submit', this.#onSubmit);
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.#onSubmit);
    }

    #onSubmit = async (event) => {
      const form = event.target.closest?.('[data-article-add]');
      if (!form) return;
      event.preventDefault();

      const scope = form.closest('.liore-article-card') ?? form.parentElement;
      const button = form.querySelector('button');
      const error = scope?.querySelector('[data-article-error]');
      if (error) error.hidden = true;
      button?.setAttribute('disabled', '');

      try {
        await addToCart({ id: Number(form.querySelector('input[name="id"]')?.value), source: 'article', routes: themeRoutes() });
        // Feature statistics (assets/liore-track.js); a no-op without consent.
        window.lioreTrack?.('article_add_to_cart', {
          product: form.dataset.statProduct || '',
          article: form.dataset.statArticle || '',
        });
        const added = scope?.querySelector('[data-article-added]');
        if (added) {
          added.hidden = false;
          // The focused button is about to disappear; keep keyboard and
          // screen-reader users on the confirmation instead of <body>.
          added.tabIndex = -1;
          added.focus({ preventScroll: true });
        }
        form.hidden = true;
      } catch {
        button?.removeAttribute('disabled');
        if (error) error.hidden = false;
      }
    };
  }

  customElements.define('liore-article-mentions', LioreArticleMentions);
}
